import { useLocation, useNavigate } from "react-router-dom";
// Before LoginForm, so the webapp CSS keeps its order: Login was the first importer of FormComponents, whose
// styles open the main bundle, then of the ui components and services/common it imports (with their styles).
// What FormComponents brought, without its fields, which stay out of the startup bundle.
import "../FormComponents/styles/index.scss";
import "@wine2trace/ui/components";
import "../../services/common";
import LoginForm from "@wine2trace/ui/LoginForm";
import { ActionLink } from "@wine2trace/ui/FormComponents";
import useLogin from "@wine2trace/auth/useLogin";
import { LOGIN } from "../../constants/apis";
import { PWD_REGEX, USER_REGEX } from "../../constants/regex"
import useAuth from "../../hooks/useAuth";
import useLabels from "../../hooks/useLabels";
import { getItem, removeItem, setItem } from "../../services/LocalStorage";
import './styles/index.scss';
import axios from "../../api/axios";
import { useEffect, useRef } from "react";
import { getWineries } from "../../services/utils";
import { useDispatch, useSelector } from "react-redux";
import { set, empty as emptyWineries } from "../../context/wineries/wineriesSlice";
import useAxiosPrivate from "../../hooks/useAxiosPrivate";
import jwt from 'jwt-decode'
import { selectActiveWinery } from "../../context/selectors";
import { buildLoginRedirectFrom, isRedirectFromValidForWinery } from "../../services/redirect";

export default function Login () {

    const dispatch = useDispatch()
    const { auth } = useAuth();
    const [getLabel] = useLabels();
    const login = useLogin({ api: axios, loginUrl: LOGIN });
    const location = useLocation();
    const navigate = useNavigate();
    const axiosPrivate = useAxiosPrivate();
    // { pathname, wineryId }: wineryId è la winery attiva quando "from" fu catturato,
    // serve a evitare di riportare l'utente su una risorsa (tank/lotto/operazione)
    // che appartiene a un'altra winery rispetto a quella selezionata dopo il login.
    const fromRef = useRef(location?.state?.from ?? buildLoginRedirectFrom(null));
    const activeWinery = useSelector(selectActiveWinery);

    useEffect(() => {
        if (Object.keys(auth)?.length > 0 && auth?.accessToken) {
            const decoded = jwt(auth?.accessToken);

            if (decoded?.exp && parseInt(Date.now() / 1000) < decoded.exp)
                navigate('/programming');
        }
    }, []);

    // Errors (login or wineries) reach LoginForm, which clears the fields and shows the message.
    const handleLogin = async (payload) => {
        const from = fromRef.current.pathname;

        const result = await login(payload);

        if (result) {
            const { accessToken, decoded } = result;

            if (decoded?.UserInfo?.role === 1) {            // ADMIN: nessuna cantina, mai
                dispatch(emptyWineries());
                navigate('/clients');
                return;
            }

            const wineries = await getWineries(axiosPrivate, accessToken, { throwOnError: true });
            
            if (!decoded?.UserInfo?.client_name?.some(w => wineries?.includes(w))) {    // if another client logs in, reset the data
                removeItem('activeWineryId');
                removeItem('wineriesList');
                dispatch(set(wineries));
            }
            if (decoded?.UserInfo?.role === 2) {            // CLIENT
                if (!wineries || wineries?.length === 0 ) {
                    navigate('/wineries/new');
                    return;
                };
                dispatch(set(wineries));

                if (
                    wineries?.length === 1 &&
                    getItem("activeWineryId") &&
                    isRedirectFromValidForWinery(fromRef.current, wineries[0]?.id)
                ) {
                    navigate(from);
                    return;
                }
                navigate('/#wineriesList', { state: { from: fromRef.current } });
            } else if (decoded?.UserInfo?.role === 3) {     // USER
                const activeWineryId = getItem("activeWineryId");
                if (!activeWineryId) {
                    navigate('/#wineriesList', { state: { from: fromRef.current } });
                    return;
                }
                if (isRedirectFromValidForWinery(fromRef.current, activeWineryId)) {
                    navigate(from, { replace: true });
                    return;
                }
                navigate('/programming');
            } else if (decoded?.UserInfo?.role === 5) {     // TANK
                const currentMonitor = Number(getItem("currentMonitor"));

                if (wineries?.length === 1) {
                    const wineryId = wineries?.[0]?.id;
                    setItem("activeWineryId", wineryId)
                    navigate(
                        isRedirectFromValidForWinery(fromRef.current, wineryId) ? from : '/programming',
                        { replace: true }
                    );

                    return;
                } else if (currentMonitor > 0) {
                    const foundWinery = wineries?.find((w) => w?.tanks?.includes(currentMonitor));
                    if (foundWinery) {
                        dispatch(set([foundWinery]));
                        setItem("activeWineryId", foundWinery?.id)
                        navigate(
                            isRedirectFromValidForWinery(fromRef.current, foundWinery?.id) ? from : '/programming',
                            { replace: true }
                        );

                        return;
                    }
                } else if (!getItem("activeWineryId")) {
                    navigate('/#wineriesList', { state: { from: fromRef.current } });
                    return;
                }
                navigate('/programming');
            }
        }
    }

    return (
        <LoginForm
            onSubmit={handleLogin}
            usernamePattern={USER_REGEX}
            passwordPattern={PWD_REGEX}
            footer={<ActionLink to="/forgot-password">{getLabel("loginForgotPassword")}</ActionLink>}
        />
    )

}
